import { Transition } from "@headlessui/react";
import { useEffect, useState } from "react";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { QUESTION_BLANK } from "../../../constants";
import KeyboardHint from "../../../fragments/KeyboardHint";
import QuestionWithBlanks from "../../../fragments/QuestionWithBlanks";
import Solver from "../../../fragments/Solver";
import Timer from "../../../fragments/Timer";
import useWebSocket from "../../../hooks/useWebSocket";
import { STATE } from "../Game";

/**
 * The main question display.
 *
 * Shows the question info, hints, timer etc.
 *
 * After the timer runs out, the state is updated to show the round winner
 * and additional information if available.
 */
const Question = ({ state, round: roundState, timer: timerState }) => {
  const [[round, timer], setLocalState] = useState([roundState, timerState]);
  const { sendJson } = useWebSocket();

  useEffect(() => {
    // Prevent updating local state if state switches out of question mode
    if (state === STATE.COOLDOWN || state === STATE.QUESTION) {
      setLocalState([roundState, timerState]);
    }
  }, [state, roundState, timerState]);

  const question = round?.question;
  const hasBlank = question.title.indexOf(QUESTION_BLANK) !== -1;

  if (state === STATE.COOLDOWN) {
    // Round is over
  }

  return (
    <div className="grid grid-cols-1 grid-rows-[minmax(0,1fr),minmax(0,2fr),minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr),minmax(0,1fr),minmax(0,1fr),minmax(0,1fr)] place-items-center w-full h-full">
      <Transition
        show={state === STATE.QUESTION}
        className="row-[1] col-[1] self-end lg:self-center"
        enter="ease-out duration-300 transition"
        enterFrom="opacity-0 scale-75"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-75"
      >
        <div className="text-gray-500">
          {question.categories.map((category, index) => (
            <span
              key={index}
              className="first:before:hidden before:content-['·'] before:text-gray-400 before:px-1"
            >
              {category}
            </span>
          ))}
        </div>
      </Transition>
      <Transition
        show={state === STATE.COOLDOWN}
        className="row-[1] col-[1] self-end lg:self-center"
        enter={`ease-out duration-300 transition ${
          round.solver ? "delay-500" : ""
        }`}
        enterFrom="opacity-0 scale-75"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-75"
      >
        {round.solver ? (
          <Solver round={round} />
        ) : (
          <p className="text-center">
            <strong>Time's up!</strong> Nobody got the answer!
          </p>
        )}
      </Transition>
      <div className="row-[2] col-[1]">
        <h1
          className={`transition duration-150 text-xl lg:text-3xl ${
            state === STATE.COOLDOWN
              ? "scale-[80%] lg:scale-[70%] -my-4"
              : "m-0"
          } max-w-prose text-center px-8`}
        >
          <QuestionWithBlanks
            title={question.title}
            hints={question.hints}
            answer={question.answer}
          />
        </h1>
        {question.image ? (
          <div
            className={`w-full max-h-32 flex flex-col items-center transition duration-150 ${
              state === STATE.COOLDOWN
                ? "scale-[60%] lg:scale-[70%] -my-8 lg:-my-6"
                : "m-0"
            }`}
            style={{ aspectRatio: question.image_aspect.toString() }}
          >
            <Zoom>
              <img
                src={question.image}
                width={question.image_width}
                height={question.image_height}
                className="w-full max-h-32"
                alt=""
              />
            </Zoom>
          </div>
        ) : null}
      </div>
      <div className="row-[3] col-[1] self-start flex flex-col items-center gap-y-2">
        <Transition
          className="flex gap-x-2"
          show={
            state === STATE.QUESTION &&
            question.hints?.filter((hint) => hint.text || !hasBlank).length > 0
          }
          enter="ease-out duration-300 transition"
          enterFrom="opacity-0 scale-75"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-75"
        >
          {question.hints
            // Only show blank hints if the question doesn't contain a blank already
            ?.filter((hint) => hint.text || !hasBlank)
            .map(({ blanks, text }, i) =>
              blanks ? (
                <KeyboardHint key={i}>{blanks}</KeyboardHint>
              ) : (
                <div className="text-center">
                  <h3 className="font-semibold text-sm">Hint:</h3>
                  {text.map((textHint, j) => (
                    <p key={j}>{textHint}</p>
                  ))}
                </div>
              )
            )}
        </Transition>
        <Transition
          show={state === STATE.QUESTION && round.hints_available > 0}
          enter="ease-out duration-300 transition"
          enterFrom="opacity-0 scale-75"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-75"
        >
          <button
            className="button"
            onClick={() => {
              sendJson({ type: "action", action: "hint" });
            }}
          >
            {gettext("Get Hint!")}
          </button>
        </Transition>
      </div>
      <Transition
        show={state === STATE.COOLDOWN}
        className="row-[3] col-[1] self-start"
        enter="ease-out duration-300 transition"
        enterFrom="opacity-0 scale-75"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-75"
      >
        <p className="text-center">
          {gettext("Correct Answer")}: <b>{question.answer}</b>
        </p>
      </Transition>
      <div className="absolute bottom-0 right-2 lg:static lg:row-[4] lg:col-[1]">
        <Timer
          {...timer}
          {...{
            urgent: state === STATE.QUESTION,
            reversed: state === STATE.COOLDOWN,
          }}
        />
      </div>
    </div>
  );
};
export default Question;
